import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FadeLink } from '@/components/generals/fade-link'
import { Reveal } from '@/components/generals/reveal'
import { ArcDivider } from '@/components/generals/hairline'
import { LearnHero } from '@/components/heroes/learn-hero'
import { DIAGRAMS } from '@/components/learn/diagrams'
import { GUIDES } from '@/constants/guides'

const Learn: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Learn'
                description='Short, illustrated guides from CosmicLens Observatory: find the North Star, photograph the Moon with a phone, understand light-years, choose a first telescope and more.'
            />

            <LearnHero contentId='guides' />

            <section id='guides' aria-label='Guides' className='container-page pb-24 pt-20 outline-none md:pt-24'>
                <Reveal variant='wipe' className='max-w-xl'>
                    <p className='text-lg text-star-2'>
                        Everything here takes less than ten minutes to read and works from any back garden. Each guide is written by the people who run our telescope nights.
                    </p>
                </Reveal>
                <ul className='mt-14 grid gap-px bg-hair md:grid-cols-2 lg:grid-cols-3'>
                    {GUIDES.map((guide, index) => {
                        const Diagram = DIAGRAMS[guide.diagram]

                        return (
                            <Reveal as='li' key={guide.slug} delay={(index % 3) * 100} className='bg-ink'>
                                <FadeLink href={`/learn/${guide.slug}`} className='group flex h-full flex-col p-6 md:p-8'>
                                    <span className='relative block aspect-[4/3] border border-hair p-4 transition-colors duration-700 group-hover:border-brass/50'>
                                        <Diagram />
                                    </span>
                                    <span className='label-mono mt-6 flex justify-between text-star-3'>
                                        <span className='text-brass'>
                                            {guide.level}
                                        </span>
                                        <span>
                                            {`${guide.minutes} min read`}
                                        </span>
                                    </span>
                                    <span className='mt-3 block font-heading text-3xl text-star transition-colors duration-500 group-hover:text-brass'>
                                        {guide.title}
                                    </span>
                                    <span className='mt-3 block text-[0.95rem] leading-relaxed text-star-2'>
                                        {guide.summary}
                                    </span>
                                </FadeLink>
                            </Reveal>
                        )
                    })}
                </ul>
            </section>

            <div className='container-page pb-32'>
                <ArcDivider label='More guides every month in Clear skies' />
            </div>
        </Layout>
    )
}

export default Learn
